import { useAssetPriceSession } from '../lib/marketData/assetPriceSessionContext.js'
import { useEffect, useRef, useState } from 'react'
import { isOCCSymbol } from '../lib/occParser.js'
import { ibkrInstrumentQuoteStore } from '../lib/marketData/ibkrInstrumentQuoteStore.js'
import { ibkrQuoteSnapshotStore } from '../lib/marketData/ibkrQuoteSnapshotStore.js'
import { resolveQuoteDisplaySession } from '../lib/marketData/ibkrQuoteSubscriptionStore.js'
import { fetchIbkrChartHistory } from '../lib/marketData/ibkrChartAdapter.js'
import { equityCloseBoundary, selectEquityCloses, resolveEquityPriceDisplay } from '../lib/marketData/equityPriceDisplay.js'
import { useEquityQuoteSessionClock } from './useIbkrQuoteSnapshot.js'

// Shared by the list and header; no independent price feed or persisted cache.
const history = new Map()
const queue = []
let running = 0
function drain() {
  while (running < 2 && queue.length) {
    running += 1
    const work = queue.shift()
    work().finally(() => { running -= 1; drain() })
  }
}
function loadCloses(symbol, now) {
  const key = `${symbol}:${equityCloseBoundary(now).key}`
  const old = history.get(key)
  if (old && old.expires > Date.now()) return old.promise
  const entry = { expires: Infinity }
  entry.promise = new Promise(resolve => {
    queue.push(async () => {
      try {
        const bars = await fetchIbkrChartHistory({ symbol, timeframe: 'daily', session: 'regular' })
        const closes = selectEquityCloses(bars, now)
        // Retry incomplete settlement/history; refresh valid references to pick
        // up provider corrections without polling on every quote or render.
        entry.value = closes
        entry.expires = Date.now() + (closes.latest && closes.previous ? 30 * 60_000 : 30_000)
        resolve(closes)
      } catch {
        entry.expires = Date.now() + 30_000
        resolve(null)
      }
    })
  })
  history.set(key, entry)
  // Bound reference retention across long-running browser sessions.
  if (history.size > 500) for (const [storedKey, value] of history) {
    if (value.expires < Date.now()) history.delete(storedKey)
  }
  drain()
  return entry.promise
}

export function useLocalEquityPriceDisplays(cards) {
  const clock = useEquityQuoteSessionClock()
  const requests = [...(cards || []).filter(card => !card.isOption && !isOCCSymbol(card.symbol))
    .filter(card => !card._quoteContract?.secType || card._quoteContract.secType === 'STK')
    .filter(card => !['future', 'futures', 'index', 'option'].includes(String(card.assetType || '').toLowerCase()))
    .map(card => ({ symbol: String(card.symbol || '').trim().toUpperCase(), instrumentId: card.instrumentId || null }))
    .filter(card => card.symbol && card.symbol !== '---')
    .reduce((bySymbol, card) => {
      if (!bySymbol.get(card.symbol)?.instrumentId || card.instrumentId) bySymbol.set(card.symbol, card)
      return bySymbol
    }, new Map()).values()].sort((a,b) => a.symbol.localeCompare(b.symbol))
  const requestsKey = JSON.stringify(requests)
  const symbolsKey = requests.map(item => item.symbol).join('|')
  const [state, setState] = useState({})
  const retained = useRef(new Map())
  const publishRef = useRef(() => {})
  useEffect(() => () => {
    retained.current.forEach(release => release())
    retained.current.clear()
  }, [])
  const boundary = equityCloseBoundary(clock).key
  useEffect(() => {
    let cancelled = false
    const symbols = symbolsKey ? symbolsKey.split('|') : []
    const bySymbol = new Map(requests.map(item => [item.symbol, item]))
    const closes = new Map(symbols.map(symbol => [symbol, history.get(`${symbol}:${boundary}`)?.value]))
    const publish = symbol => {
      if (cancelled) return
      const id = bySymbol.get(symbol)?.instrumentId
      const snapshot = id ? ibkrInstrumentQuoteStore.getSnapshot(id) : ibkrQuoteSnapshotStore.getSnapshot(symbol)
      const prepared = snapshot?.referenceCloses
      const references = id ? selectEquityCloses([prepared?.latest, prepared?.previous]
        .filter(Boolean).map(value => ({ time: String(value.date).replaceAll('-', ''), close: value.price })), clock) : closes.get(symbol)
      const display = resolveEquityPriceDisplay(snapshot, references, clock, resolveQuoteDisplaySession(snapshot, clock))
      if (id) display.referenceState = prepared?.state || 'preparing'
      setState(previous => ({ ...previous, [symbol]: { boundary, instrumentId: id || null, display } }))
    }
    publishRef.current = publish
    const identity = item => item.instrumentId ? `instrument:${item.instrumentId}` : `ticker:${item.symbol}`
    const wanted = new Set(requests.map(identity))
    for (const [symbol, release] of retained.current) {
      if (!wanted.has(symbol)) { release(); retained.current.delete(symbol) }
    }
    for (const item of requests) {
      const key = identity(item)
      if (!retained.current.has(key)) retained.current.set(key, item.instrumentId
        ? ibkrInstrumentQuoteStore.retain(item.instrumentId, () => publishRef.current(item.symbol))
        : ibkrQuoteSnapshotStore.retain(item.symbol, {}, () => publishRef.current(item.symbol)))
    }
    const refresh = () => {
      for (const symbol of symbols) {
        publish(symbol)
        if (!bySymbol.get(symbol)?.instrumentId) loadCloses(symbol, clock).then(value => { if (!cancelled) { closes.set(symbol, value); publish(symbol) } })
      }
    }
    refresh()
    const timer = setInterval(refresh, 30_000)
    return () => { cancelled = true; clearInterval(timer) }
  }, [requestsKey, boundary, clock])
  return Object.fromEntries((symbolsKey ? symbolsKey.split('|') : []).map(symbol => [symbol,
    state[symbol]?.boundary === boundary && state[symbol]?.instrumentId === (requests.find(item => item.symbol === symbol)?.instrumentId || null) ? state[symbol].display : resolveEquityPriceDisplay(null, null, clock, null),
  ]))
}

export function useEquityPriceDisplays(cards) {
  const shared = useAssetPriceSession()
  const extra = useLocalEquityPriceDisplays(shared && !shared.ready ? [] : shared ? cards.filter(card => !shared.displays[String(card.symbol || '').toUpperCase()]) : cards)
  return shared ? { ...extra, ...shared.displays } : extra
}
